import {yupResolver} from '@hookform/resolvers/yup';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {Resolver, useForm} from 'react-hook-form';
import {useStoreContext} from '../../../../../store/store-context';
import {get, ref} from 'firebase/database';
import {database} from '../../../../../utils/firebase-request';
import {useQuery, useQueryClient} from 'react-query';
import {CompanyCheckinsController} from '../../../company-legacy/company-components/company-checkins/company-checkins.controller';
import {
  ICurrentCheckinsForm,
  ICurrentCheckinsInfoResponseData,
  ISpecificCheckinsWithResponseData,
} from '../../../company-legacy/company-components/company-checkins/company-checkins.interface';
import {CompanyCheckinsRequestImpl} from '../../../company-legacy/company-components/company-checkins/company-checkins.request';
import {YouCheckinsValidatorSchema} from './you-checkins.validator';
import {beamMasteryTasks} from '../../../../../utils/firebase-request';
import {
  dateParamsToday,
  getDayOfWeekIndexFromString,
  getWeekOfYear,
} from '../../../../../utils/date';
import {activateNotification} from '../../../../../ui/molecules/notification/activate-notification';
import {usePostHogHook} from '@hooks/post-hog';
import {generateObjectId} from '../../../../../utils/objectId';
import {useLocation} from 'react-router';
import {
  resolveParticipantCycleMetadata,
  resolveParticipantCheckinRoute,
  selectParticipantCheckin,
  useParticipantCheckinMetadata,
} from '../../../checkins/my_checkins/hooks/useParticipantCheckinMetadata';
import {
  FlushableAutosave,
  submitAfterAutosaves,
} from '../../../checkins/my_checkins/hooks/flushable-autosave';
import {
  ensureQuestionResponseIds,
  getLatestCycleCheckin,
  isCheckinSuperseded,
  markCheckinSubmitted,
} from '../../../checkins/my_checkins/checkin-submit.utils';

// Type guard to check if currentCheckin has valid data
const isValidCheckin = (
  checkin: any,
): checkin is
  | ICurrentCheckinsInfoResponseData
  | ISpecificCheckinsWithResponseData => {
  return checkin && typeof checkin === 'object' && checkin !== null;
};

export function useYouCheckinsHook() {
  const queryClient = useQueryClient();

  const request = useMemo(() => new CompanyCheckinsRequestImpl(), []);
  const controller = useMemo(() => new CompanyCheckinsController(request), [
    request,
  ]);

  const location = useLocation();

  const routeSelection = useMemo(
    () => resolveParticipantCheckinRoute(location.search, location.state),
    [location.search, location.state],
  );

  const {
    authStore: {auth},
    usersStore: {currentUser},
    checkinsStore: {
      userGoals,
      setUserGoals,
      configuration,
      requiredUserQuestions,
      setRequiredUserQuestions,
      updateRequiredUserQuestionsError,
      updatePulseValidationError,
      workspaceCheckins,
      workspaceCheckinsList,
      setWorkspaceCheckins,
      setActiveWorkspaceCheckinId,
    },
  } = useStoreContext();

  const storedCurrentCheckin = useMemo(() => {
    const selectedFromList = selectParticipantCheckin(
      workspaceCheckinsList,
      routeSelection.checkinId,
    );
    if (selectedFromList) return selectedFromList;

    if (
      workspaceCheckins?.id &&
      (!routeSelection.checkinId ||
        workspaceCheckins.id === routeSelection.checkinId)
    ) {
      return workspaceCheckins;
    }

    return null;
  }, [routeSelection.checkinId, workspaceCheckins, workspaceCheckinsList]);

  const {
    data: currentCheckinResult,
    isLoading: isLoadingCurrentCheckin,
  } = useQuery<{
    selected: ICurrentCheckinsInfoResponseData | null;
    checkins: ICurrentCheckinsInfoResponseData[];
  }>(
    ['participant-current-checkin', routeSelection.checkinId],
    async () => {
      const checkins =
        (await controller.getCurrentCheckinsInfo({include: 'cycle'}, true)) ||
        [];
      return {
        selected: selectParticipantCheckin(checkins, routeSelection.checkinId),
        checkins,
      };
    },
    {
      enabled: !storedCurrentCheckin || workspaceCheckinsList.length === 0,
      initialData: storedCurrentCheckin
        ? {
            selected: storedCurrentCheckin,
            checkins: workspaceCheckinsList,
          }
        : undefined,
      onSuccess: ({checkins}) => {
        setActiveWorkspaceCheckinId(routeSelection.checkinId || null);
        setWorkspaceCheckins(checkins);
      },
      refetchOnWindowFocus: false,
    },
  );

  const currentCheckin = currentCheckinResult?.selected || storedCurrentCheckin;
  const participantCheckins =
    currentCheckinResult?.checkins?.length
      ? currentCheckinResult.checkins
      : workspaceCheckinsList;
  const isSuperseded = isCheckinSuperseded(
    currentCheckin as ICurrentCheckinsInfoResponseData | null,
    participantCheckins,
  );
  const latestCycleCheckin = getLatestCycleCheckin(
    currentCheckin as ICurrentCheckinsInfoResponseData | null,
    participantCheckins,
  );

  const {
    metadata: participantMetadata,
    isLoading: isLoadingCycleData,
  } = useParticipantCheckinMetadata(
    workspaceCheckinsList.length
      ? workspaceCheckinsList
      : currentCheckin
      ? [currentCheckin]
      : [],
    !!currentCheckin,
  );

  const cycleData = resolveParticipantCycleMetadata(
    currentCheckin,
    participantMetadata,
  );

  const normalizedQuestionsForCurrentCheckin = useMemo(
    () =>
      ensureQuestionResponseIds(
        isValidCheckin(currentCheckin)
          ? currentCheckin.responses?.questions?.questions
          : [],
      ),
    [currentCheckin],
  );

  const requiredQuestionsForCurrentCheckin = useMemo(() => {
    return normalizedQuestionsForCurrentCheckin.filter(
      (item: any) => item?.optional === false || item?.required === true,
    );
  }, [normalizedQuestionsForCurrentCheckin]);

  useEffect(() => {
    setRequiredUserQuestions(
      new Map(
        requiredQuestionsForCurrentCheckin.map((item: any) => [
          item?.questionId,
          item,
        ]),
      ),
    );
  }, [requiredQuestionsForCurrentCheckin, setRequiredUserQuestions]);

  useEffect(() => {
    return () => {
      updatePulseValidationError(null);
    };
  }, [updatePulseValidationError]);

  const isEditable =
    isValidCheckin(currentCheckin) && Object.keys(currentCheckin).length > 0;

  const parseEditValues = useCallback(() => {
    if (isEditable && isValidCheckin(currentCheckin)) {
      return {
        pulse: {
          mood: currentCheckin?.responses?.pulse?.mood ?? null,
          feedback: currentCheckin?.responses?.pulse?.feedback ?? null,
          id: currentCheckin?.responses?.pulse?.id ?? '',
        },
        priorities: {
          currentCheckin: currentCheckin?.responses?.priorities?.currentCheckin,
          nextCheckin: currentCheckin?.responses?.priorities?.nextCheckin,
          id: currentCheckin?.responses?.priorities?.id,
        },
        questions: {
          questions: normalizedQuestionsForCurrentCheckin,
          id: currentCheckin?.responses?.questions?.id,
        },
        feedback: currentCheckin?.feedback,
      };
    }
  }, [isEditable, currentCheckin, normalizedQuestionsForCurrentCheckin]);

  const {
    register,
    handleSubmit,
    errors,
    formState,
    setValue,
    control,
    watch,
    clearErrors,
    reset,
  } = useForm<ICurrentCheckinsForm>({
    resolver: yupResolver(YouCheckinsValidatorSchema.youCheckins) as Resolver<
      ICurrentCheckinsForm,
      object
    >,
    mode: 'all',
    defaultValues: {
      pulse: {
        mood: isEditable ? parseEditValues()?.pulse?.mood : null,
        feedback: isEditable ? parseEditValues()?.pulse?.feedback : null,
        id: isEditable ? parseEditValues()?.pulse?.id : '',
      },
      priorities: {
        currentCheckin:
          isEditable && !!parseEditValues()?.priorities?.currentCheckin
            ? parseEditValues()?.priorities?.currentCheckin
            : [],
        nextCheckin:
          isEditable && !!parseEditValues()?.priorities?.nextCheckin
            ? parseEditValues()?.priorities?.nextCheckin
            : [],
        id: isEditable ? parseEditValues()?.priorities?.id : '',
      },
      questions: {
        questions: isEditable ? parseEditValues()?.questions?.questions : [],
        id: isEditable ? parseEditValues()?.questions?.id : '',
      },
      feedback: isEditable ? (parseEditValues()?.feedback as any) : '',
    },
  });

  const isSubmitting = useMemo(() => {
    return formState.isSubmitting;
  }, [formState]);

  const hasError = useMemo(() => {
    return formState.submitCount > 1 && formState.errors;
  }, [formState.errors, formState.submitCount]);

  const handleFormValueChange = useCallback(
    (value: any, name: string) => {
      setValue(name, value);
    },
    [setValue],
  );

  const [isReadOnly, setIsReadOnly] = useState(false);

  const allowsLateSubmission = configuration?.allowLateSubmission;

  useEffect(() => {
    if (isValidCheckin(currentCheckin)) {
      if (currentCheckin.isSubmitted || routeSelection.mode === 'view') {
        setIsReadOnly(true);
        return;
      }

      if (isSuperseded) {
        setIsReadOnly(true);
        return;
      }

      if (routeSelection.mode === 'edit') {
        setIsReadOnly(false);
        return;
      }

      if (
        currentCheckin?.year === new Date().getFullYear() &&
        currentCheckin?.weekOfYear === getWeekOfYear()
      ) {
        if (allowsLateSubmission) return;

        if (
          dateParamsToday().dayOfWeekIndex <=
          getDayOfWeekIndexFromString(currentCheckin?.dueDay)
        ) {
          setIsReadOnly(!!currentCheckin?.isSubmitted);
        } else {
          setIsReadOnly(true);
        }
      } else {
        setIsReadOnly(true);
      }
    }
  }, [
    currentCheckin,
    isSuperseded,
    allowsLateSubmission,
    routeSelection.mode,
    setIsReadOnly,
  ]);

  const {postHogCapture} = usePostHogHook();

  const insertDefaultCheckinsData = useCallback(async () => {
    const parsedValues = parseEditValues();
    if (isValidCheckin(currentCheckin)) {
      reset({
        pulse: {
          mood: parsedValues?.pulse?.mood ?? null,
          feedback: parsedValues?.pulse?.feedback ?? null,
          id: parsedValues?.pulse?.id ?? '',
        },
        priorities: {
          currentCheckin: currentCheckin?.responses?.priorities?.currentCheckin
            ? currentCheckin?.responses?.priorities?.currentCheckin
            : [],
          nextCheckin: currentCheckin?.responses?.priorities?.nextCheckin
            ? currentCheckin?.responses?.priorities?.nextCheckin
            : [],
          id: currentCheckin?.responses?.priorities?.id,
        },
        questions: {
          questions: normalizedQuestionsForCurrentCheckin,
          id: currentCheckin?.responses?.questions?.id,
        },
        feedback: parsedValues?.feedback ? (parsedValues?.feedback as any) : '',
      });
    }
  }, [
    parseEditValues,
    reset,
    currentCheckin,
    normalizedQuestionsForCurrentCheckin,
  ]);

  useEffect(() => {
    insertDefaultCheckinsData();
  }, [insertDefaultCheckinsData]);

  const persistPulse = useCallback(
    async (data: {
      pulse: {mood: string; feedback: string};
      pulseId: string;
    }) => {
      if (!data?.pulseId) {
        return true;
      }
      return controller.updateCheckinsPulseResponse(
        {
          feedback:
            data?.pulse?.feedback !== null && data?.pulse?.feedback !== ''
              ? data.pulse.feedback
              : undefined,
          mood: data.pulse.mood !== null ? data.pulse.mood : undefined,
        },
        data.pulseId,
      );
    },
    [controller],
  );
  const pulseAutosave = useMemo(
    () => new FlushableAutosave(persistPulse, 500),
    [persistPulse],
  );
  const updatePulse = useCallback(
    (data: Parameters<typeof persistPulse>[0]) => pulseAutosave.saveNow(data),
    [pulseAutosave],
  );
  const debounceUpdatePulse = useCallback(
    (data: Parameters<typeof persistPulse>[0]) => pulseAutosave.schedule(data),
    [pulseAutosave],
  );

  const persistPriorities = useCallback(
    async (data: {
      priorities: {
        currentCheckin?: {
          text?: string;
          completed?: boolean;
        }[];
        nextCheckin?: {
          text?: string;
          completed?: boolean;
        }[];
      };
      prioritiesId: string;
    }) => {
      return controller.updateCheckinsPrioritiesResponse(
        {
          currentCheckin: data.priorities?.currentCheckin?.filter(
            (checkin: {text?: string; completed?: boolean}) =>
              checkin?.text !== '',
          ),
          nextCheckin: data.priorities?.nextCheckin
            ?.filter(
              (checkin: {text?: string; completed?: boolean}) =>
                checkin?.text !== '',
            )
            .map((checkin: {text?: string; completed?: boolean}) => ({
              text: checkin?.text,
              completed: checkin?.completed,
            })),
        },
        data.prioritiesId,
      );
    },
    [controller],
  );
  const prioritiesAutosave = useMemo(
    () => new FlushableAutosave(persistPriorities, 2000),
    [persistPriorities],
  );
  const debounceUpdatePriorities = useCallback(
    (data: Parameters<typeof persistPriorities>[0]) =>
      prioritiesAutosave.schedule(data),
    [prioritiesAutosave],
  );
  const updatePriorities = useCallback(
    (data: Parameters<typeof persistPriorities>[0]) =>
      prioritiesAutosave.saveNow(data),
    [prioritiesAutosave],
  );

  const persistQuestions = useCallback(
    async (data: {
      questions: {
        questions: {
          question: string;
          response?: null | string;
          target: {type: string; ids: string[]};
        }[];
      };
      questionId: string;
    }) => {
      return controller.updateCheckinsQuestionsResponse(
        {
          questions: data.questions.questions.map(
            (question: {
              question: string;
              questionId?: string | null;
              response?: null | string;
              target: {type: string; ids: string[]};
            }) => ({
              ...question,
              questionId:
                question.questionId === null || !question.questionId
                  ? generateObjectId()
                  : question.questionId,
              _id: undefined,
              error: undefined,
              required: undefined,
              response:
                question.response === null ? undefined : question.response,
            }),
          ),
        },
        data.questionId,
      );
    },
    [controller],
  );
  const questionsAutosave = useMemo(
    () => new FlushableAutosave(persistQuestions, 1000),
    [persistQuestions],
  );
  const debounceUpdateQuestions = useCallback(
    (data: Parameters<typeof persistQuestions>[0]) =>
      questionsAutosave.schedule(data),
    [questionsAutosave],
  );
  const updateQuestions = useCallback(
    (data: Parameters<typeof persistQuestions>[0]) =>
      questionsAutosave.saveNow(data),
    [questionsAutosave],
  );

  useEffect(
    () => () => {
      void pulseAutosave.dispose();
      void prioritiesAutosave.dispose();
      void questionsAutosave.dispose();
    },
    [prioritiesAutosave, pulseAutosave, questionsAutosave],
  );

  const flushPendingResponseUpdates = useCallback(async () => {
    const results = await Promise.all([
      pulseAutosave.flush(),
      prioritiesAutosave.flush(),
      questionsAutosave.flush(),
    ]);

    return results.every(Boolean);
  }, [prioritiesAutosave, pulseAutosave, questionsAutosave]);

  const submitForm = useCallback(
    async (data: any) => {
      if (isSuperseded) {
        activateNotification({
          title: 'Check-in closed',
          content:
            'This check-in is no longer available because a newer check-in has been sent.',
          kind: 'error',
        });
        return false;
      }

      const pulseIsRequired =
        isValidCheckin(currentCheckin) &&
        currentCheckin.params?.includes('pulse') &&
        currentCheckin.responses?.pulse?.required !== false;
      const hasMissingRequiredQuestion = Array.from(requiredUserQuestions).some(
        (q: any) => !q?.[1].response,
      );
      const hasMissingRequiredPulse =
        pulseIsRequired && !(data?.pulse?.mood?.length > 1);

      if (hasMissingRequiredQuestion || hasMissingRequiredPulse) {
        activateNotification({
          title: 'Error',
          content: 'Kindly complete required fields',
          kind: 'error',
        });

        Array.from(requiredUserQuestions).forEach((q: any) => {
          if (!q?.[1].response?.length) {
            updateRequiredUserQuestionsError(q[0]);
          }
        });
        if (hasMissingRequiredPulse) {
          updatePulseValidationError('Kindly select a pulse');
        }
        return false;
      }

      if (!isValidCheckin(currentCheckin) || !currentCheckin.id) {
        return false;
      }

      const submitted = await submitAfterAutosaves({
        flush: flushPendingResponseUpdates,
        submit: () =>
          controller.submitSpecificCheckin(
            {},
            currentCheckin.id as string,
            currentCheckin.isSubmitted ? 'update' : 'submit',
          ),
      });

      if (!submitted) return false;

      if (workspaceCheckinsList.some((checkin) => checkin.id === currentCheckin.id)) {
        setWorkspaceCheckins(
          markCheckinSubmitted(workspaceCheckinsList, currentCheckin.id),
        );
      }
      setIsReadOnly(true);
      Array.from(requiredUserQuestions).forEach((q: any) => {
        updateRequiredUserQuestionsError(q[0], true);
      });

      await Promise.all([
        queryClient.invalidateQueries([
          'participant-current-checkin',
          routeSelection.checkinId,
        ]),
        queryClient.invalidateQueries(['user-checkins-list']),
        queryClient.invalidateQueries(['my-checkins']),
      ]);

      if (auth.user.role === 'user' && !auth.user.isReviewer) {
        beamMasteryTasks('complete-checkin', true);
      }

      postHogCapture('checkin_submitted');

      get(ref(database, `onboarding/${currentUser?.id}/step/checkins`))
        .then((snapshot: any) => {
          if (snapshot?.val() !== true) {
            postHogCapture('checkin_onboarding_completed');
          }
        })
        .catch((error) => {
          console.error('Error getting onboarding checkins step:', error);
        });

      return true;
    },
    [
      isSuperseded,
      auth.user.isReviewer,
      auth.user.role,
      controller,
      currentCheckin,
      currentUser?.id,
      flushPendingResponseUpdates,
      postHogCapture,
      queryClient,
      requiredUserQuestions,
      routeSelection.checkinId,
      setWorkspaceCheckins,
      updatePulseValidationError,
      updateRequiredUserQuestionsError,
      workspaceCheckinsList,
    ],
  );

  return {
    register,
    handleSubmit,
    errors,
    formState,
    setValue,
    control,
    watch,
    clearErrors,
    reset,
    isSubmitting,
    allowsLateSubmission,
    hasError,
    handleFormValueChange,
    submitForm,
    updatePulse,
    debounceUpdatePulse,
    debounceUpdatePriorities,
    updatePriorities,
    debounceUpdateQuestions,
    updateQuestions,
    setIsReadOnly,
    isReadOnly,
    userGoals,
    setUserGoals,
    // New data from queries
    currentCheckin,
    isSuperseded,
    latestCycleCheckin,
    cycleData,
    isLoadingCurrentCheckin,
    isLoadingCycleData,
  };
}
